import { create } from 'zustand';
import { KanbanBoard, KanbanList, KanbanCard } from '../services/kanbanService';
import { arrayMove } from '@dnd-kit/sortable';

interface KanbanState {
  currentBoard: KanbanBoard | null;
  boards: KanbanBoard[];
  isLoading: boolean;
  error: string | null;

  // Actions
  setCurrentBoard: (board: KanbanBoard | null) => void;
  setBoards: (boards: KanbanBoard[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Board actions
  addBoard: (board: KanbanBoard) => void;
  updateBoard: (id: string, updates: Partial<KanbanBoard>) => void;
  removeBoard: (id: string) => void;

  // List actions
  addList: (list: KanbanList) => void;
  updateList: (id: string, updates: Partial<KanbanList>) => void;
  removeList: (id: string) => void;
  reorderLists: (startIndex: number, endIndex: number) => void;

  // Card actions
  addCard: (card: KanbanCard) => void;
  updateCard: (id: string, updates: Partial<KanbanCard>) => void;
  removeCard: (id: string) => void;
  moveCard: (
    cardId: string,
    sourceListId: string,
    destinationListId: string,
    destinationIndex: number
  ) => void;
  reorderCardInList: (listId: string, startIndex: number, endIndex: number) => void;
}

export const useKanbanStore = create<KanbanState>((set, get) => ({
  currentBoard: null,
  boards: [],
  isLoading: false,
  error: null,

  setCurrentBoard: (board) => set({ currentBoard: board }),
  setBoards: (boards) => set({ boards }),
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),

  addBoard: (board) =>
    set((state) => ({
      boards: [...state.boards, board],
    })),

  updateBoard: (id, updates) =>
    set((state) => ({
      boards: state.boards.map((board) =>
        board.id === id ? { ...board, ...updates } : board
      ),
      currentBoard:
        state.currentBoard?.id === id
          ? { ...state.currentBoard, ...updates }
          : state.currentBoard,
    })),

  removeBoard: (id) =>
    set((state) => ({
      boards: state.boards.filter((board) => board.id !== id),
      currentBoard: state.currentBoard?.id === id ? null : state.currentBoard,
    })),

  addList: (list) =>
    set((state) => {
      if (!state.currentBoard) return state;
      return {
        currentBoard: {
          ...state.currentBoard,
          lists: [...(state.currentBoard.lists || []), list],
        },
      };
    }),

  updateList: (id, updates) =>
    set((state) => {
      if (!state.currentBoard) return state;
      return {
        currentBoard: {
          ...state.currentBoard,
          lists: (state.currentBoard.lists || []).map((list) =>
            list.id === id ? { ...list, ...updates } : list
          ),
        },
      };
    }),

  removeList: (id) =>
    set((state) => {
      if (!state.currentBoard) return state;
      return {
        currentBoard: {
          ...state.currentBoard,
          lists: (state.currentBoard.lists || []).filter((list) => list.id !== id),
        },
      };
    }),

  reorderLists: (startIndex, endIndex) =>
    set((state) => {
      if (!state.currentBoard || !state.currentBoard.lists) return state;

      const lists = arrayMove(state.currentBoard.lists, startIndex, endIndex);
      // Atualizar posições
      const updatedLists = lists.map((list, index) => ({
        ...list,
        position: index,
      }));

      return {
        currentBoard: {
          ...state.currentBoard,
          lists: updatedLists,
        },
      };
    }),

  addCard: (card) =>
    set((state) => {
      if (!state.currentBoard) return state;
      const lists = (state.currentBoard.lists || []).map((list) =>
        list.id === card.list_id
          ? {
              ...list,
              cards: [...(list.cards || []), card],
            }
          : list
      );

      return {
        currentBoard: {
          ...state.currentBoard,
          lists,
        },
      };
    }),

  updateCard: (id, updates) =>
    set((state) => {
      if (!state.currentBoard) return state;
      const lists = (state.currentBoard.lists || []).map((list) => ({
        ...list,
        cards: (list.cards || []).map((card) =>
          card.id === id ? { ...card, ...updates } : card
        ),
      }));

      return {
        currentBoard: {
          ...state.currentBoard,
          lists,
        },
      };
    }),

  removeCard: (id) =>
    set((state) => {
      if (!state.currentBoard) return state;
      const lists = (state.currentBoard.lists || []).map((list) => ({
        ...list,
        cards: (list.cards || []).filter((card) => card.id !== id),
      }));

      return {
        currentBoard: {
          ...state.currentBoard,
          lists,
        },
      };
    }),

  moveCard: (cardId, sourceListId, destinationListId, destinationIndex) =>
    set((state) => {
      if (!state.currentBoard) return state;

      // Encontrar o cartão
      let cardToMove: KanbanCard | null = null;
      const sourceList = (state.currentBoard.lists || []).find(
        (list) => list.id === sourceListId
      );
      if (sourceList) {
        const cardIndex = (sourceList.cards || []).findIndex((card) => card.id === cardId);
        if (cardIndex !== -1) {
          cardToMove = sourceList.cards![cardIndex];
        }
      }

      if (!cardToMove) return state;

      // Remover da lista de origem
      const lists = (state.currentBoard.lists || []).map((list) => {
        if (list.id === sourceListId) {
          return {
            ...list,
            cards: (list.cards || []).filter((card) => card.id !== cardId),
          };
        }
        return list;
      });

      // Adicionar à lista de destino
      const updatedLists = lists.map((list) => {
        if (list.id === destinationListId) {
          const cards = [...(list.cards || [])];
          const updatedCard = { ...cardToMove!, list_id: destinationListId };
          cards.splice(destinationIndex, 0, updatedCard);

          // Atualizar posições
          const cardsWithPositions = cards.map((card, index) => ({
            ...card,
            position: index,
          }));

          return {
            ...list,
            cards: cardsWithPositions,
          };
        }
        return list;
      });

      return {
        currentBoard: {
          ...state.currentBoard,
          lists: updatedLists,
        },
      };
    }),

  reorderCardInList: (listId, startIndex, endIndex) =>
    set((state) => {
      if (!state.currentBoard) return state;
      const lists = (state.currentBoard.lists || []).map((list) => {
        if (list.id === listId && list.cards) {
          const cards = arrayMove(list.cards, startIndex, endIndex);
          // Atualizar posições
          const cardsWithPositions = cards.map((card, index) => ({
            ...card,
            position: index,
          }));
          return {
            ...list,
            cards: cardsWithPositions,
          };
        }
        return list;
      });

      return {
        currentBoard: {
          ...state.currentBoard,
          lists,
        },
      };
    }),
}));


